import type { Dossier } from '../../../../services/dossier/dossier.service';
import type { AuditTrailEntry } from '../../../../services/workflow-engine/generated/models/audit-trail-entry';
import type { StepForm } from '../../../../services/workflow-engine/generated/models/step-form';
import type { StepCandidateOption } from '../engine/engine-step-panel/engine-step-panel.component';

export interface Uc2EngineAction {
  id: string;
  description: string | null;
  status: string;
  actionTaken: string | null;
  observation: string | null;
}

export interface Uc2ProposedAction {
  id: string;
  candidateId: string;
  candidateTitle: string;
  description: string;
}

function objectOf(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export function uc2AnalystReturn(entries: readonly AuditTrailEntry[], activeStepKey: string | null | undefined) {
  if (activeStepKey !== 'analyst_gate') return null;
  const latestEntry = [...entries]
    .filter(entry => entry.eventKind === 'step-entered' && entry.toStepKey === 'analyst_gate')
    .sort((left, right) => Date.parse(left.occurredOnUtc) - Date.parse(right.occurredOnUtc))
    .at(-1);
  if (latestEntry?.outcomeToken !== 'returned' || latestEntry.fromStepKey !== 'engineer_gate') return null;
  return {
    actorOid: latestEntry.actorOid,
    occurredOnUtc: latestEntry.occurredOnUtc,
    reason: textOf(objectOf(latestEntry.payload)['reason']),
  };
}

export function buildUc2EngineView(dossier: Dossier | null, entries: readonly AuditTrailEntry[], form: StepForm | null) {
  const candidates: StepCandidateOption[] = [...(dossier?.candidates ?? [])]
    .sort((left, right) => left.rank - right.rank)
    .map(candidate => ({
      id: candidate.candidateId,
      rank: candidate.rank,
      label: textOf(candidate.explanation['title']) ?? 'Cause explanation unavailable',
      family: textOf(candidate.explanation['family']),
      rationale: textOf(candidate.explanation['rationale']),
    }));
  const records: Record<string, Record<string, unknown>> = {};
  const descriptions = new Map<string, string>();
  const proposedActions: Uc2ProposedAction[] = [];
  for (const candidate of [...(dossier?.candidates ?? [])].sort((left, right) => left.rank - right.rank)) {
    const actions = candidate.explanation['recommendedActions'];
    for (const raw of Array.isArray(actions) ? actions : []) {
      const action = objectOf(raw);
      const id = textOf(action['id']);
      const description = textOf(action['description']);
      if (id && description) {
        descriptions.set(id, description);
        proposedActions.push({
          id, candidateId: candidate.candidateId,
          candidateTitle: textOf(candidate.explanation['title']) ?? 'Cause explanation unavailable', description,
        });
      }
    }
  }
  const completions = new Map<string, { outcome: string; actionTaken: string | null; observation: string | null }>();
  let resolvedIds: string[] = [];
  for (const entry of [...entries].sort((left, right) => Date.parse(left.occurredOnUtc) - Date.parse(right.occurredOnUtc))) {
    if (entry.eventKind === 'step-entered' && entry.toStepKey === 'analyst_gate' && entry.outcomeToken === 'returned') {
      delete records['analyst_decision'];
      delete records['approval'];
    }
    if (entry.eventKind !== 'step-record') continue;
    const payload = objectOf(entry.payload);
    const captures = objectOf(payload['captures']);
    for (const [record, value] of Object.entries(captures)) {
      records[record] = objectOf(value);
    }
    if (payload['forEach'] === 'execute_actions' && Array.isArray(payload['items'])) {
      resolvedIds = payload['items'].filter((value): value is string => typeof value === 'string');
    }
    const itemId = textOf(payload['itemId']);
    if (itemId) {
      const completion = objectOf(captures['action_completion']);
      completions.set(itemId, {
        outcome: entry.outcomeToken ?? 'recorded',
        actionTaken: textOf(completion['actionTaken']),
        observation: textOf(completion['observation']),
      });
    }
  }
  const activeItem = form?.item;
  if (activeItem?.id && activeItem.description) descriptions.set(activeItem.id, activeItem.description);
  const ids = [...new Set([...resolvedIds, ...completions.keys(), ...(activeItem?.id ? [activeItem.id] : [])])];
  const actions: Uc2EngineAction[] = ids.map(id => ({
    id, description: descriptions.get(id) ?? null,
    status: completions.get(id)?.outcome ?? (activeItem?.id === id ? 'active' : 'pending'),
    actionTaken: completions.get(id)?.actionTaken ?? null,
    observation: completions.get(id)?.observation ?? null,
  }));
  const initialValues: Record<string, string> = {};
  for (const capture of form?.captures ?? []) {
    if (capture.record === 'action_completion') continue;
    for (const field of capture.fields) {
      const value = records[capture.record]?.[field.name];
      if (typeof value === 'string') initialValues[`${capture.record}__${field.name}`] = value;
    }
  }
  const analyst = records['analyst_decision'] ?? {};
  const selectedId = textOf(analyst['selectedCandidate']);
  const candidateViews = candidates.map(candidate => ({
    ...candidate, recommendedActions: proposedActions.filter(action => action.candidateId === candidate.id),
  }));
  const selectedCandidate = candidateViews.find(candidate => candidate.id === selectedId) ?? null;
  return {
    candidates: candidateViews,
    proposedActions, actions, initialValues,
    selectedCandidateId: selectedId,
    selectedCandidate,
    selectedCause: selectedCandidate?.label ?? null,
    assessment: textOf(analyst['assessedAssessment']),
    validity: textOf(analyst['validity']),
    dismissReason: textOf(analyst['dismissReason']),
    reclassReason: textOf(analyst['reclassReason']),
    claimReason: textOf(analyst['claimReason']),
    approvalRationale: textOf(records['approval']?.['rationale']),
  };
}
